import {Component, OnInit} from '@angular/core';
import {AuthService} from '../../services/auth.service';
import {UserService} from "../../services/user.service";
import {AnswerService} from "../../services/answer.service";
import {QuestionService} from "../../services/question.service";
import {User} from "../../dtos/user";
import {Trait} from "../../dtos/trait";
import {HttpErrorResponse} from "@angular/common/http";
import {ActivatedRoute, Router} from "@angular/router";
import {SafeHtml} from "@angular/platform-browser";
import {Answer} from "../../dtos/answer";
import {Question} from "../../dtos/question";
import {ToastrService} from "ngx-toastr";
import {Globals} from "../../global/globals";
import {forkJoin} from "rxjs";

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit {

  user: User = {
    id: null,
    firstName: "",
    lastName: "",
    birthday: null,
    jobrole: "",
    email: "",
    role: "",
    department: null,
  }

  answered: boolean = true;
  answeredCount = 0;
  totalQuestions = 0;
  progressLoaded = false;
  // the three strongest process traits — the only group where a high score means strength
  topProcessTraits: Trait[] = [];

  // circumference of the progress ring (r = 52)
  readonly ringCircumference = 2 * Math.PI * 52;

  errorMessage: SafeHtml = '';
  error = false;
  showDeleteConfirmation: boolean = true;

  constructor(public authService: AuthService,
              public userService: UserService,
              public answerService: AnswerService,
              private questionService: QuestionService,
              public globals: Globals,
              private router: Router,
              private route: ActivatedRoute,
              private notification: ToastrService) {
  }

  get progress(): number {
    return this.totalQuestions ? Math.round(100 * this.answeredCount / this.totalQuestions) : 0;
  }

  get ringOffset(): number {
    return this.ringCircumference * (1 - this.progress / 100);
  }

  get roleLabel(): string {
    if (this.authService.isAdmin()) {
      return 'Administrator';
    }
    if (this.authService.isManager()) {
      return 'Manager';
    }
    return 'Team Member';
  }

  ngOnInit(): void {
    if (!this.authService.isLoggedIn()) {
      return;
    }
    this.route.data.subscribe(data => {
      this.getUser(data);
    });
  }


  onLoggedIn() {
    // the home page stays mounted when logging in from the embedded form
    this.answered = true;
    this.progressLoaded = false;
    this.topProcessTraits = [];
    this.getUser(null);
  }

  getUser(data: any) {
    let email = this.getEmail();
    if (!email)
      email = this.authService.getLoggedInEmail();
    forkJoin({
      questions: this.questionService.getAllQuestions(),
      answers: this.answerService.getAnswers(email)
    }).subscribe({
      next: ({questions, answers}: { questions: Question[]; answers: Answer[] }) => {
        const questionIds = new Set(questions.map(q => q.id));
        this.totalQuestions = questions.length;
        this.answeredCount = (answers ?? [])
          .filter(a => questionIds.has(a.question?.id) && a.answer != null && a.answer > 0)
          .length;
        this.answered = this.totalQuestions > 0 && this.answeredCount >= this.totalQuestions;
        this.progressLoaded = true;
        if (this.answered) {
          this.getTopTraits(email);
        }
      },
      error: err => {
        this.answered = false;
        this.progressLoaded = true;
      }
    });
    this.userService.getUserByEmail(email).subscribe({
      next: data => {
        this.user = data;
      },
      error: err => {
        this.error = true;
        if (err instanceof HttpErrorResponse) {
          if (err.status === 500) {
            this.errorMessage = err.error;
            this.router.navigate(['/500']).then(r => console.log('Error status 500'));
          } else if (err.status === 404) {
            this.errorMessage = `<strong>Problems fetching User!</strong> ${err.error}`;
            this.router.navigate(['/404']).then(r => console.log('Error status 404'));
          } else if (err.status === 401) {
            this.errorMessage = `<strong>Problems fetching User!</strong> ${err.error}`;
            this.router.navigate(['/401']).then(r => console.log('Error status 401'));
          } else {
            this.router.navigate(['/login']).then(r => console.log('Login'));
          }
        }
      }
    });
  }

  private getTopTraits(email: string) {
    this.questionService.getAssessment(email).subscribe({
      next: traits => {
        // backend order: 6 process, 4 neuro, 7 psycho traits
        this.topProcessTraits = traits.slice(0, 6)
          .sort((a, b) => b.resultPercentage - a.resultPercentage)
          .slice(0, 3);
      },
      error: () => {
        this.topProcessTraits = [];
      }
    });
  }

  getEmail(): string {
    return this.route.snapshot.paramMap.get('email');
  }
}
